import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@mui/material";

import { CustomIconButton } from "../../../components/CustomIconButton/CustomIconButton";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import { CustomVehicleInfos } from "../../../components/CustomVehicleInfos/CustomVehicleInfos";
import { colors } from "../../../styles/colors";
import { CustomVehicleContrat } from "../../../components/CustomVehicleContrat/CustomVehicleContrat";
import { CustomCost } from "../../../components/CustomCost/CustomCost";
import useStyles from "./styles";
import { useGetTotalTCOByVehicle } from "../../../api/tco";
import { getCurrentFleetId } from "../../../utils/getCurrentFleet";
import { formatEuros } from "../../../utils/money";

type props = {
  /**
   * Required. The panel used to render CustomVehicleInfos and
   * CustomVehicleContrat with no vehicle at all: both fell back to
   * useParams().id, which does not exist on /tco, so they requested
   * /fleets/{fleet}/vehicles/NaN and rendered empty fields under a heading
   * that said "Result".
   */
  vehicleId: number;
  onClose?: () => void;
};

export const ResultSearchVehicle: FC<props> = ({ vehicleId, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { totalTcoByVehicle, isLoading, isError } = useGetTotalTCOByVehicle(
    getCurrentFleetId(),
    vehicleId
  );

  return (
    <div className={classes.divResultSearch}>
      <div className={classes.divTextResult}>
        <CustomTypography
          text={t("common.result", "Result")}
          variant={"default"}
          fontWeight={"bold"}
        />
        <CustomIconButton
          color={colors.BLACK}
          backgroundColor={colors.default}
          widthIcon={"25px"}
          heightIcon={"25px"}
          iconName="close"
          onClick={onClose}
        />
      </div>

      <CustomVehicleInfos vehicleId={vehicleId} />
      <CustomVehicleContrat vehicleId={vehicleId} />

      <div className={classes.divContentTCO}>
        {isLoading ? (
          <>
            <Skeleton variant="rounded" height={90} className={classes.skeletonCard} />
            <Skeleton variant="rounded" height={90} className={classes.skeletonCard} />
          </>
        ) : isError ? (
          // A vehicle with no active rental contract legitimately 404s on this
          // endpoint. Saying so beats showing € 0,00 as if it were measured.
          <CustomTypography
            text={t(
              "fleet.noCostForVehicle",
              "No cost data for this vehicle — it has no active contract."
            )}
            variant={"default"}
            color={colors.mutedText}
          />
        ) : (
          <>
            <CustomCost
              label={t(
                "panel.average.averageMonthlyCost",
                "AVERAGE MONTHLY COST"
              )}
              value={formatEuros(totalTcoByVehicle?.total_monthly_cost)}
            />
            <CustomCost
              label={t("panel.average.totalCost", "TOTAL COST")}
              value={formatEuros(totalTcoByVehicle?.total_tco_cost)}
            />
          </>
        )}
      </div>
    </div>
  );
};
